import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api, i18n } from "@/lib/api";

const ACADEMIC_FORMATS = [
  { id: "pdf", labelKey: "exportPdf" },
  { id: "docx", labelKey: "exportDocx" },
  { id: "pptx", labelKey: "exportPptx" },
];

const DESIGN_STYLES = [
  { id: "auto", labelKey: "styleAuto" },
  { id: "editorial_light", labelKey: "styleEditorialLight" },
  { id: "midnight", labelKey: "styleMidnight" },
  { id: "high_contrast", labelKey: "styleHighContrast" },
];

function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export default function VaultLibrary({ language, items, currentUserId, onRefresh }) {
  const t = i18n[language];
  const [openId, setOpenId] = useState(null);
  const [openDoc, setOpenDoc] = useState(null);
  const [renameId, setRenameId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  // exportPicker: { id, mode: 'download'|'email' }
  const [exportPicker, setExportPicker] = useState(null);
  // designStyle applies to PowerPoint exports
  const [designStyle, setDesignStyle] = useState("auto");
  // emailComposer: { id, format, to }
  const [emailComposer, setEmailComposer] = useState(null);
  // driveLink: last successful Google Drive web view link
  const [driveLink, setDriveLink] = useState(null);
  // Archive recovery view
  const [showArchived, setShowArchived] = useState(false);
  const [archivedItems, setArchivedItems] = useState([]);
  const [loadingArchived, setLoadingArchived] = useState(false);

  const openItem = async (id) => {
    setOpenId(id);
    try {
      const r = await api.getDocument(id);
      setOpenDoc(r.document);
    } catch (e) {
      setOpenDoc(null);
    }
  };

  const rename = async (id) => {
    if (!renameValue.trim()) return;
    setBusy(true);
    try {
      await api.patchDocument(id, { custom_document_name: renameValue.trim() });
      setRenameId(null);
      setRenameValue("");
      onRefresh?.();
    } finally {
      setBusy(false);
    }
  };

  const downloadFormat = async (id, fmt) => {
    setBusy(true);
    setStatus(t.exporting);
    try {
      const { blob, filename } = await api.exportDocumentUrl(id, fmt, designStyle);
      triggerBlobDownload(blob, filename);
      setStatus("");
      setExportPicker(null);
    } catch (e) {
      const detail =
        e?.response?.data?.detail ||
        (typeof e?.response?.data === "string" ? e.response.data : null) ||
        e?.message ||
        "Download failed";
      setStatus(detail);
    } finally {
      setBusy(false);
    }
  };

  const sendEmailAttachment = async () => {
    if (!emailComposer) return;
    const { id, format, to } = emailComposer;
    if (!to || !to.trim()) {
      setStatus(language === "es" ? "Ingresa un correo." : "Enter an email.");
      return;
    }
    setBusy(true);
    setStatus(t.sendingEmail);
    try {
      await api.emailExportDocument(id, {
        to_email: to.trim(),
        format,
        style: designStyle,
      });
      setStatus(t.emailSent);
      setEmailComposer(null);
      setExportPicker(null);
    } catch (e) {
      const detail =
        e?.response?.data?.detail ||
        (typeof e?.response?.data === "string" ? e.response.data : null) ||
        e?.message ||
        t.emailFailed;
      setStatus(detail);
    } finally {
      setBusy(false);
    }
  };

  const syncToDrive = async (id, fmt) => {
    setBusy(true);
    setDriveLink(null);
    setStatus(t.syncingDrive);
    try {
      const r = await api.syncDriveDocument(id, { format: fmt, style: designStyle });
      setStatus(t.syncedDrive);
      setDriveLink(r?.web_view_link || null);
      setExportPicker(null);
    } catch (e) {
      const detail =
        e?.response?.data?.detail ||
        (typeof e?.response?.data === "string" ? e.response.data : null) ||
        e?.message ||
        "Google Drive sync failed";
      setStatus(detail);
    } finally {
      setBusy(false);
    }
  };

  const shareWithColleague = async (id) => {
    const to = window.prompt(language === "es" ? "Correo del colega:" : "Colleague email:");
    if (!to) return;
    const referralLink = currentUserId
      ? `${window.location.origin}/?ref=${currentUserId}`
      : `${window.location.origin}/`;
    setBusy(true);
    setStatus(t.sendingEmail);
    try {
      // Share-with-colleague keeps the inline/plain-text body so the referral link is prominent.
      await api.emailDocument({ document_id: id, to_email: to, referral_link: referralLink });
      setStatus(language === "es" ? "Compartido" : "Shared");
    } catch (e) {
      const detail =
        e?.response?.data?.detail ||
        e?.message ||
        (language === "es" ? "Fallo al compartir" : "Share failed");
      setStatus(detail);
    } finally {
      setBusy(false);
    }
  };

  const togglePublic = async (item) => {
    const next = !item.is_public_template;
    setBusy(true);
    try {
      await api.patchDocument(item.document_id, { is_public_template: next });
      onRefresh?.();
    } finally {
      setBusy(false);
    }
  };

  const archive = async (id) => {
    setBusy(true);
    try {
      await api.patchDocument(id, { archived: true });
      onRefresh?.();
      if (showArchived) loadArchived();
    } finally {
      setBusy(false);
    }
  };

  const loadArchived = async () => {
    setLoadingArchived(true);
    try {
      const r = await api.listDocuments(true);
      setArchivedItems(r.items || []);
    } catch (e) {
      setArchivedItems([]);
    } finally {
      setLoadingArchived(false);
    }
  };

  const toggleArchivedView = async () => {
    const next = !showArchived;
    setShowArchived(next);
    if (next) await loadArchived();
  };

  const unarchive = async (id) => {
    setBusy(true);
    setStatus(t.unarchiving);
    try {
      await api.unarchiveDocument(id);
      setArchivedItems((prev) => prev.filter((d) => d.document_id !== id));
      setStatus(t.unarchived);
      onRefresh?.();
    } catch (e) {
      const detail =
        e?.response?.data?.detail ||
        e?.message ||
        (language === "es" ? "Fallo al desarchivar" : "Unarchive failed");
      setStatus(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="vault-library">
      <div className="h-eyebrow">{t.vault}</div>
      <h2 className="h-display" style={{ fontSize: "1.4rem", margin: "0.4rem 0 1.2rem" }}>
        {language === "es" ? "Biblioteca de Documentos" : "Academic Document Vault"}
      </h2>

      {items.length === 0 && (
        <p className="txt-mute" data-testid="vault-empty">
          {language === "es"
            ? "Tu biblioteca esta vacia. Las generaciones aparecen aqui automaticamente."
            : "Your library is empty. Generations appear here automatically."}
        </p>
      )}

      {items.map((item) => (
        <div className="vault-row" key={item.document_id} data-testid={`vault-row-${item.document_id}`}>
          <div>
            <button
              data-testid={`vault-open-${item.document_id}`}
              className="btn-link"
              style={{ fontSize: "1.02rem", color: "var(--ink)", borderBottom: "1px solid var(--ink-faint)" }}
              onClick={() => openItem(item.document_id)}
            >
              {item.custom_document_name || item.document_title}
            </button>
            <div className="kbd" style={{ marginTop: "0.25rem" }}>
              {item.document_type} - {new Date(item.created_at).toLocaleDateString()}
            </div>
            {renameId === item.document_id && (
              <div style={{ marginTop: "0.5rem", display: "flex", gap: "0.5rem", alignItems: "center" }}>
                <input
                  data-testid={`vault-rename-input-${item.document_id}`}
                  className="input-line"
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  placeholder={language === "es" ? "Nuevo nombre" : "New name"}
                />
                <button
                  data-testid={`vault-rename-save-${item.document_id}`}
                  className="btn-ghost"
                  disabled={busy}
                  onClick={() => rename(item.document_id)}
                >
                  {t.save}
                </button>
              </div>
            )}

            {exportPicker?.id === item.document_id && (
              <div
                data-testid={`vault-export-picker-${item.document_id}`}
                style={{ marginTop: "0.6rem", display: "flex", flexDirection: "column", gap: "0.35rem" }}
              >
                <div className="kbd" style={{ marginBottom: "0.2rem" }}>
                  {(exportPicker.mode === "email"
                    ? t.emailAs
                    : exportPicker.mode === "drive"
                    ? t.syncDrive
                    : t.exportAs)}{" "}
                  - {t.chooseFormat}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.2rem", marginBottom: "0.35rem" }}>
                  <label className="kbd" htmlFor={`vault-style-${item.document_id}`}>
                    {t.designStyle}
                  </label>
                  <select
                    id={`vault-style-${item.document_id}`}
                    data-testid={`vault-style-select-${item.document_id}`}
                    className="input-line"
                    value={designStyle}
                    onChange={(e) => setDesignStyle(e.target.value)}
                    style={{ maxWidth: "260px" }}
                  >
                    {DESIGN_STYLES.map((s) => (
                      <option key={s.id} value={s.id}>
                        {t[s.labelKey]}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ display: "flex", gap: "0.85rem", flexWrap: "wrap" }}>
                  {ACADEMIC_FORMATS.map((f) => (
                    <button
                      key={f.id}
                      data-testid={`vault-${exportPicker.mode}-${f.id}-${item.document_id}`}
                      className="btn-link"
                      disabled={busy}
                      onClick={() => {
                        if (exportPicker.mode === "download") {
                          downloadFormat(item.document_id, f.id);
                        } else if (exportPicker.mode === "drive") {
                          syncToDrive(item.document_id, f.id);
                        } else {
                          setEmailComposer({ id: item.document_id, format: f.id, to: "" });
                        }
                      }}
                    >
                      {t[f.labelKey]}
                    </button>
                  ))}
                  <button
                    data-testid={`vault-export-cancel-${item.document_id}`}
                    className="btn-link"
                    onClick={() => {
                      setExportPicker(null);
                      setEmailComposer(null);
                    }}
                  >
                    {t.cancel}
                  </button>
                </div>

                {emailComposer?.id === item.document_id && (
                  <div
                    data-testid={`vault-email-composer-${item.document_id}`}
                    style={{ marginTop: "0.4rem", display: "flex", flexDirection: "column", gap: "0.45rem" }}
                  >
                    <div className="kbd">
                      {t.emailAs}: {t[ACADEMIC_FORMATS.find((f) => f.id === emailComposer.format).labelKey]}
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                      <input
                        data-testid={`vault-email-input-${item.document_id}`}
                        type="email"
                        className="input-line"
                        placeholder={t.emailTo}
                        value={emailComposer.to}
                        onChange={(e) =>
                          setEmailComposer((prev) => ({ ...prev, to: e.target.value }))
                        }
                        style={{ minWidth: "240px" }}
                      />
                      <button
                        data-testid={`vault-email-send-${item.document_id}`}
                        className="btn-ghost"
                        disabled={busy}
                        onClick={sendEmailAttachment}
                      >
                        {t.sendEmail}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.45rem", alignItems: "flex-end" }}>
            <button
              data-testid={`vault-rename-${item.document_id}`}
              className="btn-link"
              onClick={() => {
                setRenameId(item.document_id);
                setRenameValue(item.custom_document_name || item.document_title || "");
              }}
            >
              {t.rename}
            </button>
            <button
              data-testid={`vault-export-${item.document_id}`}
              className="btn-link"
              onClick={() => {
                setExportPicker({ id: item.document_id, mode: "download" });
                setEmailComposer(null);
              }}
            >
              {t.exportAs}
            </button>
            <button
              data-testid={`vault-email-toggle-${item.document_id}`}
              className="btn-link"
              onClick={() => {
                setExportPicker({ id: item.document_id, mode: "email" });
                setEmailComposer(null);
              }}
            >
              {t.emailAs}
            </button>
            <button
              data-testid={`vault-drive-toggle-${item.document_id}`}
              className="btn-link"
              onClick={() => {
                setExportPicker({ id: item.document_id, mode: "drive" });
                setEmailComposer(null);
                setDriveLink(null);
              }}
            >
              {t.syncDrive}
            </button>
            {item.is_public_template && item.public_share_slug && (
              <a
                data-testid={`vault-public-link-${item.document_id}`}
                className="btn-link"
                href={`${window.location.origin}/template/${item.public_share_slug}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t.publicViewPage}
              </a>
            )}
            <button
              data-testid={`vault-share-${item.document_id}`}
              className="btn-link"
              onClick={() => shareWithColleague(item.document_id)}
            >
              {t.share}
            </button>
            <button
              data-testid={`vault-publish-${item.document_id}`}
              className="btn-link"
              onClick={() => togglePublic(item)}
            >
              {item.is_public_template ? (language === "es" ? "Despublicar" : "Unpublish") : t.publishTemplate}
            </button>
            <button
              data-testid={`vault-archive-${item.document_id}`}
              className="btn-link"
              onClick={() => archive(item.document_id)}
            >
              {language === "es" ? "Archivar" : "Archive"}
            </button>
          </div>
        </div>
      ))}

      {status && (
        <div className="txt-mute" style={{ marginTop: "0.75rem", fontSize: "0.9rem" }} data-testid="vault-status">
          {status}
          {driveLink && (
            <>
              {" "}
              <a
                data-testid="vault-drive-link"
                href={driveLink}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "var(--ink)", borderBottom: "1px solid var(--ink-faint)" }}
              >
                {t.openInDrive}
              </a>
            </>
          )}
        </div>
      )}

      <div className="line" style={{ marginTop: "1.5rem" }} />
      <button
        data-testid="vault-toggle-archived"
        className="btn-link"
        onClick={toggleArchivedView}
        style={{ marginTop: "0.75rem", color: "var(--ink)", borderBottom: "1px solid var(--ink-faint)" }}
      >
        {showArchived ? t.hideArchived : t.showArchived}
      </button>

      {showArchived && (
        <div data-testid="vault-archived-list" style={{ marginTop: "1rem" }}>
          <div className="h-eyebrow">{t.archivedHeading}</div>
          <p className="txt-mute" data-testid="vault-archive-policy" style={{ marginTop: "0.35rem", fontSize: "0.85rem" }}>
            {t.archivePolicy}
          </p>
          {loadingArchived && (
            <p className="txt-mute" data-testid="vault-archived-loading" style={{ marginTop: "0.5rem" }}>
              {language === "es" ? "Cargando..." : "Loading..."}
            </p>
          )}
          {!loadingArchived && archivedItems.length === 0 && (
            <p className="txt-mute" data-testid="vault-archived-empty" style={{ marginTop: "0.5rem" }}>
              {t.archivedEmpty}
            </p>
          )}
          {!loadingArchived &&
            archivedItems.map((item) => (
              <div className="vault-row" key={item.document_id} data-testid={`vault-archived-row-${item.document_id}`}>
                <div>
                  <div style={{ fontSize: "1.02rem", color: "var(--ink)" }}>
                    {item.custom_document_name || item.document_title}
                  </div>
                  <div className="kbd" style={{ marginTop: "0.25rem" }}>
                    {item.document_type} - {new Date(item.created_at).toLocaleDateString()}
                  </div>
                </div>
                <button
                  data-testid={`vault-unarchive-${item.document_id}`}
                  className="btn-link"
                  disabled={busy}
                  onClick={() => unarchive(item.document_id)}
                >
                  {t.unarchive}
                </button>
              </div>
            ))}
        </div>
      )}

      {openId && openDoc && (
        <div
          data-testid="vault-detail-drawer"
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            width: "min(640px, 100vw)",
            background: "var(--bg)",
            borderLeft: "1px solid var(--ink-faint)",
            padding: "2rem 1.5rem",
            zIndex: 50,
            overflowY: "auto",
          }}
        >
          <div className="h-eyebrow">{openDoc.document_type}</div>
          <h2 className="h-display" style={{ fontSize: "1.3rem", margin: "0.3rem 0" }}>
            {openDoc.custom_document_name || openDoc.document_title}
          </h2>
          <div className="line" />
          <article className="markdown-body">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{openDoc.processed_analysis || ""}</ReactMarkdown>
          </article>
          <div className="line-strong" />
          <button data-testid="vault-detail-close" className="btn-ghost" onClick={() => { setOpenId(null); setOpenDoc(null); }}>
            Close
          </button>
        </div>
      )}
    </div>
  );
}
